import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { now, toISO } from 'lib/utils/date';
import _get from 'lodash/get';
import queryString from 'query-string';
import routes from 'lib/utils/routes';

import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import {
  actionSetLaborTicketActionLoading,
  actionSetLaborTicketParts,
  actionSetPendingActivityTypeRef,
} from 'lib/actions';
import { throwErrorToast } from 'lib/utils/toast';
import { getWorkOrderManagementRedirectPath } from 'lib/selectors/getWorkOrderRedirectPath';

import { getHasOpenSetupActivity } from 'lib/selectors/getHasOpenSetupActivity';
import useRequestJobCloseAt from 'lib/hooks/useRequestJobCloseAt';
import usePauseOperation from './useHandlePauseOperation';
import usePartAdjustment from './hooks/usePartAdjustment';
import useEditLaborTicket from './hooks/useEditLaborTicket';
import useOperatorLogout from './hooks/useOperatorLogout';
import useStopRun from './hooks/useStopRun';
import useSubmitChangeActivities from './useSubmitChangeActivities';
import useCloseOrderOperaiton from './hooks/useCloseOrderOperation';

const useSubmitLaborTicket = ({ form } = {}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = queryString.parse(history.location.search);

  const editLaborTicket = useEditLaborTicket();
  const workOrderManagementRedirectPath = useSelector(
    getWorkOrderManagementRedirectPath
  );
  const requestOperatorLogout = useOperatorLogout();
  const requestStopRun = useStopRun();
  const submitChangeActivities = useSubmitChangeActivities({ form });
  const closeOrderOperation = useCloseOrderOperaiton();
  const { submitPartAdjustment: requestPartAdjustment } = usePartAdjustment({
    form,
  });
  const requestPauseOperation = usePauseOperation({ onPaused: false });

  const machine = useSelector(getMachine);

  const laborTicketAction = useSelector(getLaborTicketAction);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const hasOpenSetupActivity = useSelector(getHasOpenSetupActivity);

  const closeOrder = form?.workOrderComplete.value && !hasOpenSetupActivity;
  const shouldStopCurrentRun = closeOrder || queryParams.startAndStop;
  const currentLaborTicket = useSelector(getCurrentLaborTicket);

  const requestJobCloseAt = useRequestJobCloseAt();

  /**
   * Submits a labor ticket with the given action and options
   * @param {Object} params - The parameters object
   * @param {string} params.action - The labor ticket action to perform
   * @param {Object} [params.opts={}] - Additional options for the labor ticket submission
   * @param {(string|number)} [params.opts.pendingActivityTypeRef] - Reference to the pending activity type
   * @param {string} [params.opts.redirectPath] - Path to redirect after submission
   * @param {boolean} [params.opts.shouldStopCurrentRun] - Whether to stop the current run
   * @param {string} [params.opts.loggingInOperator] - ID of the operator logging in
   * @param {boolean} [params.opts.switchToUnattendedOperator] - Whether to switch to unattended operator
   * @param {boolean} [params.opts.closeOrderOperation] - Whether to close the order operation via override
   * @param {string} [params.opts.clockOutTime] - Timestamp when the labor ticket should be closed/ended
   * @returns {Promise<void|any>} Returns undefined in some cases, history.push() result in others, or the result of submitLaborTicket
   */
  const submitLaborTicket = async ({ opts = {} }) => {
    const redirectPath = opts.redirectPath || workOrderManagementRedirectPath;
    const isCloseOrderOperation = opts?.closeOrderOperation ?? closeOrder;

    if (!currentWorkOrderOp) {
      return;
    }
    const clockOutTime =
      opts?.clockOutTime || opts?.startAt || toISO(now().valueOf());
    try {
      if (currentLaborTicket && form) {
        await requestPartAdjustment();

        const rejectedParts = form.rejectedParts.value || 0;
        const goodParts = form.totalParts.value - rejectedParts;
        const reason = form.rejectReason.value;

        await editLaborTicket(
          {
            clockOut: clockOutTime,
            state: 'CLOSED',
            goodParts,
            badParts: rejectedParts,
            comment: form.comment.value || null,
          },
          currentLaborTicket.laborTicketRef,
          reason?.reasonRef
        );
      }

      if (isCloseOrderOperation) {
        await closeOrderOperation(
          { closedDate: clockOutTime },
          currentWorkOrderOp.workOrderOperationRef
        );
      }

      if (redirectPath) {
        history.push(redirectPath);
      }
      dispatch(actionSetLaborTicketParts(0));
    } catch (e) {
      dispatch(actionSetLaborTicketActionLoading(false));
      throwErrorToast(`Could not submit labor ticket: ${e}`);
    }
  };

  const requestStopRunWrapper = async () => {
    try {
      const closedAt = await requestJobCloseAt();
      const result = await requestStopRun({ closedAt });
      return { result, closedAt };
    } catch (error) {
      dispatch(actionSetLaborTicketActionLoading(false));
      return null;
    }
  };

  const requestLaborTicketAction = async ({ action, opts = {} } = {}) => {
    const isStopCurrentRun = opts?.shouldStopCurrentRun ?? shouldStopCurrentRun;

    switch (action) {
      case LABOR_TICKET_ACTIONS.STOP_RUN: {
        const stopRunResult = await requestStopRunWrapper();
        if (!stopRunResult) return null;
        return submitLaborTicket({
          action,
          opts: {
            ...opts,
            clockOutTime: stopRunResult.closedAt,
          },
        });
      }

      case LABOR_TICKET_ACTIONS.PAUSE_SETUP: {
        await requestPauseOperation();
        return submitLaborTicket({ action, opts });
      }

      case LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT: {
        await requestOperatorLogout();
        let closedAt;
        if (isStopCurrentRun) {
          const stopRunResult = await requestStopRunWrapper();
          if (!stopRunResult) return null;
          closedAt = stopRunResult.closedAt;
        }
        if (opts?.shouldPauseSetup) {
          await requestPauseOperation();
        }
        await submitLaborTicket({
          action,
          opts: {
            ...opts,
            ...(closedAt && { clockOutTime: closedAt }),
          },
        });
        return history.push({
          pathname: routes.machineIdHomePath(machine.id),
        });
      }

      case LABOR_TICKET_ACTIONS.CHANGE_ACTIVITIES: {
        return submitChangeActivities({ opts });
      }

      default:
        return null;
    }
  };

  return async ({ action, opts = {} } = {}) => {
    dispatch(actionSetLaborTicketActionLoading(true));

    try {
      if (_get(opts, 'pendingActivityTypeRef')) {
        await dispatch(
          actionSetPendingActivityTypeRef(_get(opts, 'pendingActivityTypeRef'))
        );
      }

      return await requestLaborTicketAction({
        action: action || laborTicketAction,
        opts,
      });
    } finally {
      dispatch(actionSetLaborTicketActionLoading(false));
    }
  };
};

export default useSubmitLaborTicket;
